import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Video,
  Image as ImageIcon,
  Trash2,
  Download,
  Eye,
  X,
  Loader2,
  AlertCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  document: {
    icon: FileText,
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    label: "Documents",
    accept: ".pdf,.doc,.docx,.txt,.csv,.xlsx",
    maxSize: "25MB",
  },
  video: {
    icon: Video,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    label: "Videos",
    accept: ".mp4,.mov,.avi,.wmv,.webm,.mkv",
    maxSize: "100MB",
  },
  image: {
    icon: ImageIcon,
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
    label: "Images",
    accept: ".jpg,.jpeg,.png,.gif,.webp",
    maxSize: "10MB",
  },
};

function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileCard({ file, isAdmin, onDelete, onPreview }) {
  const config = CATEGORY_CONFIG[file.category] || CATEGORY_CONFIG.document;
  const Icon = config.icon;
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${file.original_filename}"?`)) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_URL}/api/admin/player/${file.athlete_id}/files/${file.file_id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success("File deleted");
        onDelete(file.file_id);
      } else {
        toast.error("Failed to delete file");
      }
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/player-files/${file.file_id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border ${config.border} ${config.bg} group hover:shadow-sm transition-shadow`}
      data-testid={`file-card-${file.file_id}`}
    >
      <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
        <Icon className={`w-5 h-5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{file.filename || file.original_filename}</p>
        <p className="text-xs text-gray-500">
          {formatFileSize(file.size)} &middot; {new Date(file.created_at).toLocaleDateString()}
          {file.uploaded_by_name && <span> &middot; by {file.uploaded_by_name}</span>}
        </p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {(file.category === "image" || file.category === "document") && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onPreview(file)} data-testid={`preview-${file.file_id}`}>
            <Eye className="w-4 h-4 text-gray-500" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDownload} data-testid={`download-${file.file_id}`}>
          <Download className="w-4 h-4 text-gray-500" />
        </Button>
        {isAdmin && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDelete} disabled={deleting} data-testid={`delete-${file.file_id}`}>
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-500" />}
          </Button>
        )}
      </div>
    </div>
  );
}

function FilePreviewModal({ file, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url = null;
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/player-files/${file.file_id}/download`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch {
        toast.error("Failed to load preview");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file.file_id]);

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-gray-900 truncate">{file.original_filename}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>
        <div className="p-4 flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-auto bg-gray-50">
          {loading ? (
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          ) : blobUrl ? (
            file.category === "image" ? (
              <img src={blobUrl} alt={file.original_filename} className="max-w-full max-h-[65vh] object-contain rounded" />
            ) : file.content_type === "application/pdf" ? (
              <iframe src={blobUrl} className="w-full h-[65vh] rounded border" title={file.original_filename} />
            ) : (
              <p className="text-gray-500">Preview not available for this file type</p>
            )
          ) : (
            <p className="text-gray-500">Failed to load preview</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminFileUpload({ athleteId, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileNames, setFileNames] = useState({});
  const fileInputRef = useRef(null);

  const handleFilesSelected = (fileList) => {
    if (!fileList?.length) return;
    const newFiles = Array.from(fileList);
    setPendingFiles(newFiles);
    const names = {};
    newFiles.forEach((f, i) => {
      const nameWithoutExt = f.name.replace(/\.[^/.]+$/, "");
      names[i] = nameWithoutExt;
    });
    setFileNames(names);
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);
    setProgress(0);

    const total = pendingFiles.length;
    let completed = 0;
    let successCount = 0;

    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      try {
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("file", file);
        if (fileNames[i]?.trim()) {
          formData.append("display_name", fileNames[i].trim());
        }

        const res = await fetch(`${API_URL}/api/admin/player/${athleteId}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (res.ok) {
          successCount++;
        } else {
          const err = await res.json();
          toast.error(`${file.name}: ${err.detail || "Upload failed"}`);
        }
      } catch (err) {
        toast.error(`${file.name}: Upload error`);
      }
      completed++;
      setProgress(Math.round((completed / total) * 100));
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded`);
      if (onUploadComplete) onUploadComplete();
    }
    setUploading(false);
    setProgress(0);
    setPendingFiles([]);
    setFileNames({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFilesSelected(e.dataTransfer.files);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-file-upload">
      {/* File Naming Step */}
      {pendingFiles.length > 0 && !uploading && (
        <div className="mb-4 border border-blue-200 rounded-xl p-4 bg-blue-50/50 space-y-3" data-testid="file-naming-step">
          <h4 className="text-sm font-semibold text-gray-800">Name your files before uploading:</h4>
          {pendingFiles.map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <FileText size={16} className="text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={fileNames[i] || ""}
                onChange={(e) => setFileNames(prev => ({ ...prev, [i]: e.target.value }))}
                className="flex-1 text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none"
                placeholder={f.name}
                data-testid={`file-name-input-${i}`}
              />
              <span className="text-xs text-gray-400 flex-shrink-0">{f.name.split('.').pop()}</span>
            </div>
          ))}
          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={() => { setPendingFiles([]); setFileNames({}); }}
              className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-200 rounded-lg transition"
            >Cancel</button>
            <button
              onClick={handleUpload}
              className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition"
              data-testid="confirm-upload-btn"
            >Upload {pendingFiles.length} file{pendingFiles.length > 1 ? "s" : ""}</button>
          </div>
        </div>
      )}

      <div
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer
          ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400 bg-gray-50"}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && !pendingFiles.length && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.mp4,.mov,.avi,.wmv,.webm,.mkv,.jpg,.jpeg,.png,.gif,.webp"
          onChange={(e) => handleFilesSelected(e.target.files)}
          data-testid="file-input"
        />
        {uploading ? (
          <div className="space-y-3">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
            <p className="text-sm font-medium text-blue-600">Uploading... {progress}%</p>
            <div className="w-48 mx-auto h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="text-sm font-medium text-gray-700">
              Drop files here or <span className="text-blue-600 underline">browse</span>
            </p>
            <p className="text-xs text-gray-500">
              PDF, DOC, Images (10MB), Videos (100MB)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function PlayerFilesGallery({ athleteId, isAdmin }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, [athleteId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchFiles() {
    try {
      const res = await fetch(`${API_URL}/api/player/${athleteId}/files`);
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const documents = files.filter((f) => f.category === "document");
  const videos = files.filter((f) => f.category === "video");
  const images = files.filter((f) => f.category === "image");
  const hasFiles = files.length > 0;

  if (!hasFiles && !isAdmin) return null;

  return (
    <div className="space-y-4" data-testid="player-files-gallery">
      {isAdmin && (
        <AdminFileUpload athleteId={athleteId} onUploadComplete={fetchFiles} />
      )}

      {!hasFiles && isAdmin && (
        <div className="text-center py-4">
          <AlertCircle className="w-6 h-6 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No files uploaded yet. Use the area above to upload.</p>
        </div>
      )}

      {documents.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4 text-red-600" /> Documents ({documents.length})
          </h4>
          <div className="space-y-2">
            {documents.map((f) => (
              <FileCard key={f.file_id} file={f} isAdmin={isAdmin} onDelete={handleDelete} onPreview={setPreviewFile} />
            ))}
          </div>
        </div>
      )}

      {videos.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Video className="w-4 h-4 text-blue-600" /> Videos ({videos.length})
          </h4>
          <div className="space-y-2">
            {videos.map((f) => (
              <VideoPlayer key={f.file_id} file={f} isAdmin={isAdmin} onDelete={handleDelete} />
            ))}
          </div>
        </div>
      )}

      {images.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-green-600" /> Images ({images.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {images.map((f) => (
              <ImageThumbnail key={f.file_id} file={f} isAdmin={isAdmin} onDelete={handleDelete} onPreview={setPreviewFile} />
            ))}
          </div>
        </div>
      )}

      {previewFile && (
        <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
}

function VideoPlayer({ file, isAdmin, onDelete }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadVideo = async () => {
    if (blobUrl) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/player-files/${file.file_id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setBlobUrl(URL.createObjectURL(blob));
    } catch {
      toast.error("Failed to load video");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 overflow-hidden" data-testid={`video-${file.file_id}`}>
      <div className="flex items-center gap-3 p-3">
        <Video className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{file.original_filename}</p>
          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
        </div>
        {!blobUrl && (
          <Button size="sm" variant="outline" onClick={loadVideo} disabled={loading} data-testid={`load-video-${file.file_id}`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
            {loading ? "" : "Play"}
          </Button>
        )}
        {isAdmin && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
            if (window.confirm(`Delete "${file.original_filename}"?`)) {
              const token = localStorage.getItem("token");
              fetch(`${API_URL}/api/admin/player/${file.athlete_id}/files/${file.file_id}`, {
                method: "DELETE", headers: { Authorization: `Bearer ${token}` }
              }).then(r => { if (r.ok) { toast.success("Deleted"); onDelete(file.file_id); } });
            }
          }}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
      </div>
      {blobUrl && (
        <video controls className="w-full max-h-[400px] bg-black" src={blobUrl}>
          Your browser does not support video playback.
        </video>
      )}
    </div>
  );
}

function ImageThumbnail({ file, isAdmin, onDelete, onPreview }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url = null;
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/player-files/${file.file_id}/download`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file.file_id]);

  return (
    <div
      className="relative group rounded-lg overflow-hidden border border-green-200 bg-green-50 aspect-square cursor-pointer"
      onClick={() => onPreview(file)}
      data-testid={`image-thumb-${file.file_id}`}
    >
      {loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : blobUrl ? (
        <img src={blobUrl} alt={file.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-gray-300" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
        <div className="w-full p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-xs text-white truncate">{file.original_filename}</p>
        </div>
      </div>
      {isAdmin && (
        <button
          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Delete "${file.original_filename}"?`)) {
              const token = localStorage.getItem("token");
              fetch(`${API_URL}/api/admin/player/${file.athlete_id}/files/${file.file_id}`, {
                method: "DELETE", headers: { Authorization: `Bearer ${token}` }
              }).then(r => { if (r.ok) { toast.success("Deleted"); onDelete(file.file_id); } });
            }
          }}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
